import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageCircle, Pin, Lock, Eye, Clock, Plus } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';

export default function DiscussionList({ discussionableType = null, discussionableId = null }) {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const { token } = useAuthStore();

  useEffect(() => {
    loadDiscussions();
  }, [discussionableType, discussionableId, currentPage]);

  const loadDiscussions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: currentPage });
      
      if (discussionableType && discussionableId) {
        params.append('discussionable_type', discussionableType);
        params.append('discussionable_id', discussionableId);
      }

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/discussions?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDiscussions(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error('Failed to load discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* New Discussion Button (when filtered by type/id) */}
      {discussionableType && discussionableId && (
        <Link
          to={`/discussions?type=${discussionableType}&id=${discussionableId}`}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Start New Discussion
        </Link>
      )}

      {discussions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No discussions yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            {discussionableType && discussionableId 
              ? 'Click the button above to start the first discussion!' 
              : 'Be the first to start a discussion!'}
          </p>
        </div>
      ) : (
        <>
          {discussions.map((discussion) => (
            <div
              key={discussion.id}
              onClick={() => navigate(`/discussions/${discussion.id}`)}
              className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {discussion.is_pinned && (
                      <Pin className="h-4 w-4 text-indigo-600" />
                    )}
                    {discussion.is_locked && (
                      <Lock className="h-4 w-4 text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 hover:text-indigo-600">
                      {discussion.title}
                    </h3>
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {discussion.content}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <MessageCircle className="h-4 w-4" />
                      <span>{discussion.posts_count} {discussion.posts_count === 1 ? 'reply' : 'replies'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{discussion.views_count} views</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>Active {formatDate(discussion.last_activity_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(discussion.user?.name || 'User')}&background=4F46E5&color=fff`}
                      alt={discussion.user?.name}
                      className="h-10 w-10 rounded-full"
                    />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{discussion.user?.name}</p>
                      <p className="text-gray-500">{formatDate(discussion.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
